/**
 * The one-shot request for the tab a tag info panel shows (`TAGS.tagInfoDefaultTab`).
 *
 * Several info panels can be mounted at once (side-by-side panels), and every one of them used to
 * take a pending request as soon as it was set, for whatever tag it happened to show. A card's
 * Documents button sets its request before the card's fetch resolves, so a panel already open on
 * another tag took it, and the panel that then showed the tag opened on Info.
 *
 * A request now names its tag and when it applies:
 * - `onOpen` (a card, OPEN_TAG): taken only when a panel's element BECOMES that tag. Never in
 *   place - even the panel already showing the tag would lose it, because the open path re-sets
 *   the element next and the element watcher resets the tab.
 * - in place (a linked-field marker inside an open panel): taken by the panel showing that tag.
 * A request without a `tagId` matches any tag, which keeps the old behaviour for bare tabs.
 */

/** Builds a request for `SET_TAG_INFO_DEFAULT_TAB`. */
export function tagInfoTabRequest(tab, { tagId = null, onOpen = false } = {}) {
  return { tab, tagId, onOpen };
}

/** What the store keeps: null, or a request object. A bare tab number is untargeted and in place. */
export function normalizeTagInfoTabRequest(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object') return { tab: value, tagId: null, onOpen: false };
  if (value.tab === null || value.tab === undefined) return null;
  return { tab: value.tab, tagId: value.tagId ?? null, onOpen: value.onOpen === true };
}

function isForTag(request, tagId) {
  return request.tagId === null || String(request.tagId) === String(tagId);
}

function take(store, request) {
  store.commit('SET_TAG_INFO_DEFAULT_TAB', null);
  return request.tab;
}

/** For a panel's element watcher: the tab requested for `tagId`, cleared once read; else null. */
export function takeTagInfoTabOnOpen(store, tagId) {
  const request = store.state.TAGS.tagInfoDefaultTab;
  if (!request || !isForTag(request, tagId)) return null;
  return take(store, request);
}

/** For a panel's request watcher: an in-place request for the tag it shows; else null. */
export function takeTagInfoTabInPlace(store, tagId) {
  const request = store.state.TAGS.tagInfoDefaultTab;
  if (!request || request.onOpen || !isForTag(request, tagId)) return null;
  return take(store, request);
}
